import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@spz/ui/components/tooltip";
import { type ReactNode, createContext, useContext } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../lib/utils";
import { ActivityAttachmentsItem } from "./activity-attachments-item";
import { ActivityCommentItem } from "./activity-comment-item";
import { IconButton } from "./button";
import { Icon } from "./icon";
import { PriorityBadge } from "./priority-badge";
import { Typography } from "./typography";
import { UserInfo } from "./user-info";
import { WorkflowBadge } from "./workflow-badge";

// Context for sharing editing state across the activity item
type ActivityItemContextValue = {
  isEditing: boolean;
};

const ActivityItemContext = createContext<ActivityItemContextValue | undefined>(
  undefined,
);

const useActivityItemContext = () => {
  const context = useContext(ActivityItemContext);
  if (!context) {
    throw new Error(
      "ActivityItem compound components must be used within ActivityItem.Root",
    );
  }
  return context;
};

// Root component that provides context and container
type RootProps = {
  children: ReactNode;
  isEditing?: boolean;
  className?: string;
};

const Root: React.FC<RootProps> = ({
  children,
  isEditing = false,
  className,
}) => {
  return (
    <ActivityItemContext.Provider value={{ isEditing }}>
      <div className={cn("flex flex-col gap-3", className)}>{children}</div>
    </ActivityItemContext.Provider>
  );
};

// Header component - provides header container with tooltip provider
type HeaderProps = {
  children: ReactNode;
  className?: string;
};

const Header: React.FC<HeaderProps> = ({ children, className }) => {
  return (
    <TooltipProvider>
      <div className={cn("flex items-center justify-between", className)}>
        {children}
      </div>
    </TooltipProvider>
  );
};

// Header.Info - displays author, date, and update information
type HeaderInfoProps = {
  author: string;
  date: string;
  updateDate?: string;
  className?: string;
};

const HeaderInfo: React.FC<HeaderInfoProps> = ({
  author,
  date,
  updateDate,
  className,
}) => {
  const { t } = useTranslation();

  return (
    <div className={cn("flex flex-col items-start space-y-1", className)}>
      <Typography className="font-semibold text-gray-900">{author}</Typography>
      <Typography className="text-gray-700 text-overline">{date}</Typography>
      {updateDate && (
        <Tooltip>
          <TooltipTrigger className="cursor-pointer text-gray-700 font-base text-sm font-normal ml-1">
            ({t("modified")})
          </TooltipTrigger>
          <TooltipContent>{updateDate}</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};

// Header.Actions - container for action buttons
type HeaderActionsProps = {
  children: ReactNode;
  className?: string;
};

const HeaderActions: React.FC<HeaderActionsProps> = ({
  children,
  className,
}) => {
  return <div className={cn("flex space-x-2", className)}>{children}</div>;
};

// Header.ViewActions - shows actions when not editing
type HeaderViewActionsProps = {
  children: ReactNode;
};

const HeaderViewActions: React.FC<HeaderViewActionsProps> = ({ children }) => {
  const { isEditing } = useActivityItemContext();

  if (isEditing) {
    return null;
  }

  return <>{children}</>;
};

// Header.EditActions - shows actions when editing
type HeaderEditActionsProps = {
  children: ReactNode;
};

const HeaderEditActions: React.FC<HeaderEditActionsProps> = ({ children }) => {
  const { isEditing } = useActivityItemContext();

  if (!isEditing) {
    return null;
  }

  return <>{children}</>;
};

// Header.EditButton - edit button
type HeaderEditButtonProps = {
  onClick: () => void;
  disabled?: boolean;
};

const HeaderEditButton: React.FC<HeaderEditButtonProps> = ({
  onClick,
  disabled,
}) => {
  return <IconButton icon="edit" onClick={onClick} disabled={disabled} />;
};

// Header.DeleteButton - delete button
type HeaderDeleteButtonProps = {
  commentId: string;
  attachmentOnly: boolean;
  onClick: (id: string, attachmentOnly: boolean, anchor: HTMLElement) => void;
  disabled?: boolean;
};

const HeaderDeleteButton: React.FC<HeaderDeleteButtonProps> = ({
  commentId,
  attachmentOnly,
  onClick,
  disabled,
}) => {
  return (
    <IconButton
      variant="secondary"
      icon="trash-2"
      onClick={(e) => onClick(commentId, attachmentOnly, e.currentTarget)}
      disabled={disabled}
    />
  );
};

// Header.ValidateButton - validate/save button
type HeaderValidateButtonProps = {
  onClick: () => void;
  disabled?: boolean;
};

const HeaderValidateButton: React.FC<HeaderValidateButtonProps> = ({
  onClick,
  disabled,
}) => {
  return <IconButton icon="check" onClick={onClick} disabled={disabled} />;
};

// Header.CancelButton - cancel button
type HeaderCancelButtonProps = {
  onClick: (anchor: HTMLElement) => void;
  disabled?: boolean;
};

const HeaderCancelButton: React.FC<HeaderCancelButtonProps> = ({
  onClick,
  disabled,
}) => {
  return (
    <IconButton
      variant="secondary"
      icon="x"
      onClick={(e) => onClick(e.currentTarget)}
      disabled={disabled}
    />
  );
};

// Comment section component
type CommentProps = {
  id: string;
  // biome-ignore lint/suspicious/noExplicitAny: RichText is not available in the UI package
  content: any;
  onContentChange?: (content: unknown) => void;
  className?: string;
};

const Comment: React.FC<CommentProps> = ({
  id,
  content,
  onContentChange,
  className,
}) => {
  const { isEditing } = useActivityItemContext();

  return (
    <div className={className}>
      <ActivityCommentItem
        id={id}
        content={content}
        isEditing={isEditing}
        isBeingEdited={isEditing}
        onContentChange={onContentChange}
      />
    </div>
  );
};

// Attachments section component
type AttachmentsProps = {
  attachments: Array<{
    id: string;
    name: string;
    iconName: string;
  }>;
  onAttachmentClick?: (attachmentId: string) => void;
  className?: string;
};

const Attachments: React.FC<AttachmentsProps> = ({
  attachments,
  onAttachmentClick = () => {},
  className,
}) => {
  if (!attachments || attachments.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      <ActivityAttachmentsItem
        attachments={attachments}
        onAttachmentClick={onAttachmentClick}
      />
    </div>
  );
};

// Section component for labeled changes (workflow, conclusion, owner)
type SectionProps = {
  children: ReactNode;
  labelKey: string;
  className?: string;
};

const Section: React.FC<SectionProps> = ({ children, labelKey, className }) => {
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <Typography
        variant="body"
        trans={labelKey}
        className="text-gray-700 italic"
      />
      {children}
    </div>
  );
};

// Workflow step change component
type WorkflowStepChangeProps = {
  from?: {
    labelKeyName: string;
    variant: string;
  } | null;
  to?: {
    labelKeyName: string;
    variant: string;
  } | null;
  className?: string;
};

const WorkflowStepChange: React.FC<WorkflowStepChangeProps> = ({
  from,
  to,
  className,
}) => {
  return (
    <Section
      labelKey="activity_item_status_section_title"
      className={className}
    >
      <div className="flex items-center gap-2">
        {from && (
          <WorkflowBadge labelKey={from.labelKeyName} variant={from.variant} />
        )}
        <Icon icon="arrow-right" className="text-gray-800" size={16} />
        {to && (
          <WorkflowBadge labelKey={to.labelKeyName} variant={to.variant} />
        )}
      </div>
    </Section>
  );
};

// Workflow conclusion change component
type WorkflowConclusionChangeProps = {
  to: {
    labelKeyName: string;
  };
  className?: string;
};

const WorkflowConclusionChange: React.FC<WorkflowConclusionChangeProps> = ({
  to,
  className,
}) => {
  return (
    <Section
      labelKey="activity_item_conclusion_section_title"
      className={className}
    >
      <WorkflowBadge labelKey={to.labelKeyName} />
    </Section>
  );
};

// Owner change component
type OwnerChangeProps = {
  from: {
    firstName: string;
    lastName: string;
    avatarUrl: string | null;
  } | null;
  to: {
    firstName: string;
    lastName: string;
    avatarUrl: string | null;
  } | null;
  className?: string;
};

const OwnerChange: React.FC<OwnerChangeProps> = ({ from, to, className }) => {
  const { t } = useTranslation();
  return (
    <Section labelKey="activity_item_owner_section_title" className={className}>
      {/* Inlined OwnerActivityItem */}
      <div className="flex items-center space-x-2">
        <UserInfo
          firstName={from?.firstName}
          lastName={from?.lastName}
          avatarUrl={from?.avatarUrl}
          fullName={
            from ? `${from.firstName} ${from.lastName}` : t("unassigned")
          }
          size="S"
        />
        <Icon icon="arrow-right" size={16} className="text-gray-800" />
        <UserInfo
          firstName={to?.firstName}
          lastName={to?.lastName}
          avatarUrl={to?.avatarUrl}
          fullName={to ? `${to.firstName} ${to.lastName}` : t("unassigned")}
          size="S"
        />
      </div>
    </Section>
  );
};

type PriorityChangeProps = {
  from: {
    id: string;
    labelKeyName: string;
    variant: string;
    symbolKeyName: string;
    order: number;
  } | null;
  to: {
    id: string;
    labelKeyName: string;
    variant: string;
    symbolKeyName: string;
    order: number;
  } | null;
  className?: string;
};

const PriorityChange: React.FC<PriorityChangeProps> = ({
  from,
  to,
  className,
}) => {
  return (
    <Section
      labelKey="activity_item_priority_section_title"
      className={className}
    >
      <div className="flex items-center gap-2">
        {from && (
          <PriorityBadge
            id={from.id}
            variant={
              from.variant as "critical" | "high" | "medium" | "low" | "none"
            }
            symbol={from.symbolKeyName}
            label={from.labelKeyName}
            size="sm"
            showLabel
          />
        )}
        <Icon icon="arrow-right" className="text-gray-800" size={16} />
        {to && (
          <PriorityBadge
            id={to.id}
            variant={
              to.variant as "critical" | "high" | "medium" | "low" | "none"
            }
            symbol={to.symbolKeyName}
            label={to.labelKeyName}
            size="sm"
            showLabel
          />
        )}
      </div>
    </Section>
  );
};

// Create Header namespace with sub-components
const HeaderNamespace = Object.assign(Header, {
  Info: HeaderInfo,
  Actions: HeaderActions,
  ViewActions: HeaderViewActions,
  EditActions: HeaderEditActions,
  EditButton: HeaderEditButton,
  DeleteButton: HeaderDeleteButton,
  ValidateButton: HeaderValidateButton,
  CancelButton: HeaderCancelButton,
});

// Export compound component
export const ActivityItem = {
  Root,
  Header: HeaderNamespace,
  Comment,
  Attachments,
  Section,
  WorkflowStepChange,
  WorkflowConclusionChange,
  OwnerChange,
  PriorityChange,
};
